/*
  ROI Calculator Component
  ------------------------------------------------------------
  Purpose
  - Interactive estimator for potential savings from AI/automation by industry.
  - Combines category/industry waste ranges, admin waste baselines, and user inputs.

  Key Inputs (UI state)
  - industry: selected industry id (must match `industryDataComplete.js`).
  - employeeCount: total employees for FTE conversions.
  - annualRevenue: used for revenue-based savings estimates.
  - automationMaturity (1–10): lower = more automated; used to scale industry waste and agent adoption.
  - processMaturity (1–10): lower = less process-oriented; used for admin waste mapping (diagnostics) and interpolation bounds.

  Calculations (high level)
  - AI Agents Average panel: uses fixed 20%/30% admin waste range, independent of Process slider.
  - Potential Annual Savings and Annual Hours: interpolate within [AI base low, AI base high] using both sliders such that (1,1) yields the high end.
  - Estimated Annual Savings (headline): a single value using aiAgentsAverage.percentLow × revenue.
  - Agent-driven impact: sums selected agents’ timeSavedFTE and costImpactRate (capped), scaled by automation factor.

  Maintenance
  - Keep labels and slider semantics in sync with product copy.
  - When ROI math changes, update this header and the JSDoc for `calculateWasteAndSavings`.
*/
import React, { useState, useEffect } from 'react';
import { industryData } from '../data/industryDataComplete';
import industryRPE from '../data/industryRPE';
import { generalAgents } from '../data/industryTasks';
import ThankYouConfirmation from './ThankYouConfirmation';
import { ContactForm, ResultsDisplay } from './calculator';
import { generateDetailedReport } from '../utils/generateDetailedReport';
import { AVAILABLE_CURRENCIES } from '../utils/formatters';
import { useLocalization } from '../context/LocalizationContext';

// Lookup helper for Industry RPE average (US midpoint) by industry id across categories
const getIndustryRPEAvg = (id) => {
  try {
    if (!id || !industryRPE) return null;
    const categories = Object.keys(industryRPE);
    for (const cat of categories) {
      const list = industryRPE[cat]?.industries || [];
      const item = list.find(x => x.id === id);
      if (item && typeof item.USrpeLow === 'number' && typeof item.USrpeHigh === 'number') {
        return (item.USrpeLow + item.USrpeHigh) / 2;
      }
    }
    return null;
  } catch { return null; }
};

/**
 * ROI calculator UI component.
 * Manages form state, agent auto-configuration, and displays savings estimates.
 * No props; fully self-contained UI module.
 */

const ROICalculator = () => {
  // eslint-disable-next-line no-unused-vars
  const { t, locale, setLocale } = useLocalization();
  
  // State for form data
  const [formData, setFormData] = useState({
    industry: '',
    employeeCount: 0,
    annualRevenue: 0,
    automationMaturity: 1,
    processMaturity: 1,
    hourlyCost: 50, // Average FTE hourly cost; editable by user
    contactInfo: {
      name: '',
      email: '',
      phone: '',
      company: '',
      website: '',
      noWebsite: false
    },
    currency: locale === 'pt-BR' ? 'BRL' : 'USD' // Default currency based on locale
  });
  
  // State for detected company info (from IP lookup)
  const [detectedCompany, setDetectedCompany] = useState(null);
  const [ipLookupDone, setIpLookupDone] = useState(false);
  // Local input state for revenue text box (keeps UX smooth while typing)
  const [revenueInput, setRevenueInput] = useState('');
  // Local input state for employee count text box
  const [employeeInput, setEmployeeInput] = useState('');
  // Collapsible sections state
  const [showIndustryAgents, setShowIndustryAgents] = useState(false);
  // Agent selection state
  const [selectedGeneralAgents, setSelectedGeneralAgents] = useState([]); // indices
  const [selectedIndustryTasks, setSelectedIndustryTasks] = useState([]); // indices
  // Auto-configuration control
  const [sliderTouched, setSliderTouched] = useState(false);
  const [autoConfigLocked, setAutoConfigLocked] = useState(false);
  
  // Note: access formData fields directly to avoid unused variable linting

  const [savings, setSavings] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [runId, setRunId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [detailedReport, setDetailedReport] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [reportLink, setReportLink] = useState(null);
  const [lastSnapshot, setLastSnapshot] = useState(null);

  // Available currencies for selection (imported from formatters.js)
  const availableCurrencies = AVAILABLE_CURRENCIES;

  // Set default currency based on locale when component mounts or locale changes
  useEffect(() => {
    if (locale === 'pt-BR' && formData.currency !== 'BRL') {
      setFormData(prev => ({
        ...prev,
        currency: 'BRL'
      }));
    } else if ((locale === 'en-US' || locale === 'es-419') && formData.currency !== 'USD') {
      setFormData(prev => ({
        ...prev,
        currency: 'USD'
      }));
    }
    // We intentionally do not add formData.currency to deps to avoid an update loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  // IP lookup for locale/currency detection on mount (quick mode - no Perplexity research)
  useEffect(() => {
    if (ipLookupDone) return;
    
    (async () => {
      try {
        // Use quick mode to skip slow Perplexity research - form loads faster
        const res = await fetch('/.netlify/functions/ip-lookup?quick=true');
        if (!res.ok) return;
        
        const data = await res.json();
        if (!data.ok) return;
        
        setIpLookupDone(true);
        
        // Update locale if detected and different (only if user hasn't manually changed it)
        if (data.detectedLocale && data.detectedLocale !== locale) {
          // Note: setLocale from context marks user intent, so we use a different approach
          // The LocalizationContext already handles geo-detection, so we skip locale here
        }
        
        // Update currency if detected
        if (data.detectedCurrency) {
          setFormData(prev => ({
            ...prev,
            currency: data.detectedCurrency
          }));
        }
        
        // Note: Company detection/prefill removed for faster form loading
        // Full company research is done during lead submission for report personalization
      } catch (e) {
        console.warn('IP lookup failed:', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync text input with numeric annualRevenue
  useEffect(() => {
    setRevenueInput(formatRevenueWithCommas(formData.annualRevenue));
    // formatRevenueWithCommas is stable in this module; excluding from deps prevents unnecessary re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.annualRevenue]);

  // Sync text input with numeric employeeCount
  useEffect(() => {
    setEmployeeInput(formatIntegerWithCommas(formData.employeeCount));
    // formatIntegerWithCommas is stable in this module; excluding from deps prevents unnecessary re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.employeeCount]);

  // Close industry-specific panel if no industry selected
  useEffect(() => {
    if (!formData.industry && showIndustryAgents) {
      setShowIndustryAgents(false);
    }
  }, [formData.industry, showIndustryAgents]);

  // Reset industry-specific selections when industry changes and allow re-auto-config
  useEffect(() => {
    setSelectedIndustryTasks([]);
    // If the user had previously touched the slider, enable auto-config to run for the new industry
    setAutoConfigLocked(false);
  }, [formData.industry]);

  // Group industries by category for better UX
  const industriesByCategory = industryData.reduce((acc, industry) => {
    if (!acc[industry.category]) {
      acc[industry.category] = [];
    }
    acc[industry.category].push(industry);
    return acc;
  }, {});

  // Determine selected industry object for industry-specific agents
  const selectedIndustry = formData.industry
    ? industryData.find(i => i.id === formData.industry)
    : null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: (name === 'employeeCount' || name === 'annualRevenue' || name === 'automationMaturity' || name === 'hourlyCost')
          ? Number(value)
          : value
      }));
      if (name === 'automationMaturity') {
        setSliderTouched(true);
      }
    }
  };

  // Auto-configure agent selections based on first slider interaction
  useEffect(() => {
    if (!sliderTouched || autoConfigLocked) return;
    const level = formData.automationMaturity;

    // General agents selection: level 1 => all, else score >= level
    const generalSelected = (() => {
      if (level <= 1) return generalAgents.map((_, idx) => idx);
      return generalAgents
        .map((g, idx) => ({ idx, score: typeof g.score === 'number' ? g.score : 5 }))
        .filter(g => g.score >= level)
        .map(g => g.idx);
    })();
    setSelectedGeneralAgents(generalSelected);

    // If no industry yet, wait to lock so we can apply defaults once the industry is chosen
    const items = selectedIndustry && Array.isArray(selectedIndustry.agents)
      ? selectedIndustry.agents
      : (selectedIndustry && Array.isArray(selectedIndustry.tasks) ? selectedIndustry.tasks : null);
    if (!items) return;

    // Industry selection logic:
    // - If agents exist: use agent.score when provided; level 1 selects all
    // - Else (string tasks): prefer explicit taskScores if provided, else heuristic by index
    const industrySelected = (() => {
      if (Array.isArray(selectedIndustry?.agents)) {
        const agents = selectedIndustry.agents;
        if (level <= 1) return agents.map((_, idx) => idx);
        const scored = agents
          .map((a, idx) => ({ idx, score: typeof a?.score === 'number' ? a.score : 5 }));
        const matches = scored.filter(t => t.score >= level).map(t => t.idx);
        if (matches.length > 0) return matches;
        // Fallback: select up to top 3 highest-scored agents
        return scored
          .sort((a, b) => b.score - a.score || a.idx - b.idx)
          .slice(0, 3)
          .map(t => t.idx);
      }
      const tasks = selectedIndustry.tasks || [];
      if (level <= 1) return tasks.map((_, idx) => idx);
      const scores = Array.isArray(selectedIndustry.taskScores) && selectedIndustry.taskScores.length === tasks.length
        ? selectedIndustry.taskScores
        : null;
      if (scores) {
        const scored = scores.map((s, idx) => ({ idx, score: typeof s === 'number' ? s : 5 }));
        const matches = scored.filter(t => t.score >= level).map(t => t.idx);
        if (matches.length > 0) return matches;
        return scored
          .sort((a, b) => b.score - a.score || a.idx - b.idx)
          .slice(0, 3)
          .map(t => t.idx);
      }
      const n = Math.max(1, tasks.length);
      const idxToScore = (idx) => Math.min(10, Math.max(2, Math.ceil(((idx + 1) / n) * 10)));
      const scored = tasks.map((_, idx) => ({ idx, score: idxToScore(idx) }));
      const matches = scored.filter(t => t.score >= level).map(t => t.idx);
      if (matches.length > 0) return matches;
      return scored
        .sort((a, b) => b.score - a.score || a.idx - b.idx)
        .slice(0, 3)
        .map(t => t.idx);
    })();
    setSelectedIndustryTasks(industrySelected);
    // Do not auto-select job samples

    // Do not lock here; allow slider-driven recalculation until user manually edits selections.
  }, [sliderTouched, autoConfigLocked, formData.automationMaturity, selectedIndustry]);

  // Helper: compute default selections for current maturity level and industry
  const computeAutoSelections = (level, selectedIndustry) => {
    // General agents selection
    const generalSelected = (() => {
      if (level <= 1) return generalAgents.map((_, idx) => idx);
      return generalAgents
        .map((g, idx) => ({ idx, score: typeof g.score === 'number' ? g.score : 5 }))
        .filter(g => g.score >= level)
        .map(g => g.idx);
    })();

    // Industry selection
    const items = selectedIndustry && Array.isArray(selectedIndustry.agents)
      ? selectedIndustry.agents
      : (selectedIndustry && Array.isArray(selectedIndustry.tasks) ? selectedIndustry.tasks : null);
    let industrySelected = [];
    if (items) {
      industrySelected = (() => {
        if (Array.isArray(selectedIndustry?.agents)) {
          const agents = selectedIndustry.agents;
          if (level <= 1) return agents.map((_, idx) => idx);
          const scored = agents
            .map((a, idx) => ({ idx, score: typeof a?.score === 'number' ? a.score : 5 }));
          const matches = scored.filter(t => t.score >= level).map(t => t.idx);
          if (matches.length > 0) return matches;
          return scored
            .sort((a, b) => b.score - a.score || a.idx - b.idx)
            .slice(0, 3)
            .map(t => t.idx);
        }
        const tasks = selectedIndustry.tasks || [];
        if (level <= 1) return tasks.map((_, idx) => idx);
        const scores = Array.isArray(selectedIndustry.taskScores) && selectedIndustry.taskScores.length === tasks.length
          ? selectedIndustry.taskScores
          : null;
        if (scores) {
          const scored = scores.map((s, idx) => ({ idx, score: typeof s === 'number' ? s : 5 }));
          const matches = scored.filter(t => t.score >= level).map(t => t.idx);
          if (matches.length > 0) return matches;
          return scored
            .sort((a, b) => b.score - a.score || a.idx - b.idx)
            .slice(0, 3)
            .map(t => t.idx);
        }
        const n = Math.max(1, tasks.length);
        const idxToScore = (idx) => Math.min(10, Math.max(2, Math.ceil(((idx + 1) / n) * 10)));
        const scored = tasks.map((_, idx) => ({ idx, score: idxToScore(idx) }));
        const matches = scored.filter(t => t.score >= level).map(t => t.idx);
        if (matches.length > 0) return matches;
        return scored
          .sort((a, b) => b.score - a.score || a.idx - b.idx)
          .slice(0, 3)
          .map(t => t.idx);
      })();
    }

    return { generalSelected, industrySelected };
  };

  // Helpers for employee input
  const formatIntegerWithCommas = (value) => {
    if (value == null || isNaN(value)) return '';
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.floor(value));
  };
  const parseIntegerInput = (value) => {
    if (!value) return 0;
    const digits = value.toString().replace(/[^0-9]/g, '');
    if (!digits) return 0;
    return parseInt(digits, 10);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.industry) return;
    
    const selectedIndustry = industryData.find(i => i.id === formData.industry);
    if (!selectedIndustry) return;
    // If no selections yet (first run), compute defaults based on current automation level
    let overrideGA = selectedGeneralAgents;
    let overrideIA = selectedIndustryTasks;
    const firstRun = (!overrideGA || overrideGA.length === 0) && (!overrideIA || overrideIA.length === 0);
    if (firstRun) {
      const { generalSelected, industrySelected } = computeAutoSelections(formData.automationMaturity, selectedIndustry);
      overrideGA = generalSelected;
      overrideIA = industrySelected;
      // Update UI state, but also use overrides immediately for calculation
      setSelectedGeneralAgents(generalSelected);
      setSelectedIndustryTasks(industrySelected);
    }

    const result = calculateWasteAndSavings(
      formData.industry,
      formData.employeeCount,
      formData.annualRevenue,
      formData.automationMaturity,
      formData.processMaturity,
      overrideGA,
      overrideIA
    );
    
    if (result) {
      setSavings(result);
      setShowResults(true);
      // Generate Detailed Report (JSON + Markdown) and keep in state for follow-up actions
      const report = generateDetailedReport({
        industryId: formData.industry,
        employeeCount: formData.employeeCount,
        annualRevenue: formData.annualRevenue,
        automationMaturity: formData.automationMaturity,
        processMaturity: formData.processMaturity,
        hourlyCost: formData.hourlyCost,
        includeGeneralAgents: true,
        topN: 10,
      });
      setDetailedReport(report);
      // Persist anonymized run for admin dashboard and capture runId
      try {
        const payload = {
          formData: {
            industry: formData.industry,
            employeeCount: formData.employeeCount,
            annualRevenue: formData.annualRevenue,
            automationMaturity: formData.automationMaturity,
            processMaturity: formData.processMaturity,
            hourlyCost: formData.hourlyCost, // FTE burn rate
            currency: formData.currency
          },
          // Capture early company if user entered it before contact submit
          contactInfo: formData.contactInfo?.company ? { company: formData.contactInfo.company } : undefined,
          locale,
          savings: {
            estimatedAnnualSavings: result?.estimatedAnnualSavings,
            estimatedAnnualSavingsFormatted: result?.estimatedAnnualSavingsFormatted,
            potentialAnnualSavingsLow: result?.potentialAnnualSavings?.low,
            potentialAnnualSavingsHigh: result?.potentialAnnualSavings?.high
          },
          // Expanded calculated snapshot for Admin/reporting
          fteSavings: result?.fteSavings || null,
          industryWaste: result?.industryWaste || null,
          adminWaste: result?.adminWaste || null,
          totalWaste: result?.totalWaste || null,
          aiAgentsAverage: result?.aiAgentsAverage || null,
          wastePercentages: result?.wastePercentages || null,
          potentialSavings: result?.potentialSavings || null,
          efficiencyScore: result?.efficiencyScore ?? null,
          revenuePerEmployee: result?.revenuePerEmployee ?? null,
          industryRPE: result?.industryRPE ?? null,
          automationPotential: result?.automationPotential,
          industryName: result?.industryName,
          agentImpact: result?.agentImpact || null,
          selections: {
            generalAgentsIdx: Array.isArray(selectedGeneralAgents) ? selectedGeneralAgents : [],
            industryAgentsIdx: Array.isArray(selectedIndustryTasks) ? selectedIndustryTasks : [],
            generalAgentsLabels: Array.isArray(selectedGeneralAgents) ? selectedGeneralAgents.map(i => (generalAgents[i]?.label ?? null)).filter(Boolean) : [],
            industryAgentsLabels: (() => {
              const items = selectedIndustry && Array.isArray(selectedIndustry.agents) ? selectedIndustry.agents : [];
              return Array.isArray(selectedIndustryTasks) ? selectedIndustryTasks.map(i => (items[i]?.label ?? null)).filter(Boolean) : [];
            })()
          },
          report: report ? { markdown: report.markdown, meta: report.meta } : null,
          calcVersion: 'v2',
          timestamp: new Date().toISOString()
        };
        // Keep a local copy of the snapshot so we can include it with contact submission
        setLastSnapshot(payload);
        const res = await fetch('/.netlify/functions/submit-result', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json().catch(() => ({}));
        // Capture the new runId from response for immediate use (state update is async)
        if (res.ok && data?.ok && data?.runId) {
          setRunId(data.runId);
          // Report is generated on-demand from the stored result JSON - no separate save needed
          setReportLink(`/.netlify/functions/get-report?runId=${encodeURIComponent(data.runId)}`);
          console.log('Run saved:', data);
        } else {
          console.warn('Run not saved:', data);
        }
      } catch (_) {
        // ignore persistence errors for UX
      }
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true); // Show processing modal
    try {
      const snapshot = lastSnapshot || {
        // Fallback minimal snapshot if user jumps straight to contact
        formData: {
          industry: formData.industry,
          employeeCount: formData.employeeCount,
          annualRevenue: formData.annualRevenue,
          automationMaturity: formData.automationMaturity,
          processMaturity: formData.processMaturity,
          hourlyCost: formData.hourlyCost,
          currency: formData.currency,
        },
        locale,
        savings: savings ? {
          estimatedAnnualSavings: savings.estimatedAnnualSavings,
          estimatedAnnualSavingsFormatted: savings.estimatedAnnualSavingsFormatted,
          potentialAnnualSavingsLow: savings.potentialAnnualSavings?.low,
          potentialAnnualSavingsHigh: savings.potentialAnnualSavings?.high,
        } : null,
        calcVersion: 'v2',
        timestamp: new Date().toISOString(),
      };
      const body = {
        runId: runId || null,
        contactInfo: { ...formData.contactInfo },
        snapshot,
        report: detailedReport ? { markdown: detailedReport.markdown, meta: detailedReport.meta } : null,
      };
      await fetch('/.netlify/functions/submit-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).catch(() => {});
    } catch {}
    setIsSubmitting(false); // Hide processing modal
    setSubmitted(true);
  };

  // Format currency based on selected currency and locale
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: formData.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Get currency symbol for display
  const getCurrencySymbol = (currency) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).formatToParts(0).find(part => part.type === 'currency').value;
  };

  // Format for revenue display per spec:
  // - Use locale-appropriate currency format with no decimals below 1M
  // - Use M with one decimal at 1M and above (M is standard across locales for millions)
  const formatRevenueDisplay = (amount) => {
    const currencySymbol = getCurrencySymbol(formData.currency);
    
    if (amount == null || isNaN(amount)) {
      return new Intl.NumberFormat(locale, { 
        style: 'currency', 
        currency: formData.currency, 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 0 
      }).format(0);
    }
    
    if (amount < 1000000) {
      return new Intl.NumberFormat(locale, { 
        style: 'currency', 
        currency: formData.currency, 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 0 
      }).format(amount);
    }
    
    // For amounts >= 1M, use standard M notation with one decimal
    const millions = amount / 1000000;
    return `${currencySymbol}${millions.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`;
  };

  // Format number with locale-appropriate separators (no decimals), without currency symbol
  const formatRevenueWithCommas = (amount) => {
    if (amount == null || isNaN(amount)) return '';
    return new Intl.NumberFormat(locale, { 
      maximumFractionDigits: 0,
      useGrouping: true
    }).format(amount);
  };

  // Parse string like "$1,234,567" or "1.2M" to number of dollars
  const parseRevenueInput = (value) => {
    if (!value) return 0;
    const v = value.toString().trim().toUpperCase();
    // Support M suffix
    const mMatch = v.match(/^\$?\s*([0-9]*\.?[0-9]+)\s*M$/i);
    if (mMatch) {
      const millions = parseFloat(mMatch[1]);
      if (!isNaN(millions)) return Math.round(millions * 1000000);
    }
    // Fallback: strip non-digits
    const digits = v.replace(/[^0-9]/g, '');
    if (!digits) return 0;
    return parseInt(digits, 10);
  };

  /**
   * Compute waste breakdown, potential savings, and agent-driven impact for current inputs.
   *
   * Steps:
   * - Derive industryWastePercent from automationMaturity (1=best ⇒ wasteLow; 10=worst ⇒ wasteHigh).
   * - Map processMaturity (1..10) to admin waste 30%..20% (diagnostic display only; AI panel uses fixed 20/30).
   * - AI Agents - Average savings panel should ALWAYS use fixed 20%/30% admin waste
   * - Interpolate Potential Annual Savings within [baseLow, baseHigh] using both sliders so (1,1) = high end.
   * - Convert to FTE hours (low only shown in UI) and compute agentImpact from selected agents with caps.
   *
   * @param {string} industryId
   * @param {number} employeeCount
   * @param {number} annualRevenue
   * @param {number} automationMaturity - 1..10
   * @param {number} processMaturity - 1..10
   * @returns {{
   *   industryWaste: { percent: number, amount: number, low: number, high: number },
   *   adminWaste: { low: number, high: number, amountLow: number, amountHigh: number },
   *   totalWaste: { low: number, high: number },
   *   aiAgentsAverage: { percentLow: number, percentHigh: number, amountLow: number, amountHigh: number },
   *   wastePercentages: { industryLow: number, industryHigh: number, adminLow: number, adminHigh: number, totalLow: number, totalHigh: number },
   *   potentialSavings: { low: number, high: number },
   *   fteSavings: { low: number, high: number },
   *   agentImpact: { laborSavings: number, revenueImpact: number, fteSaved: number, employeeSatisfactionImprovement: number, total: number, inputs: { selectedCount: number, timeSavedAdj: number, costImpactAdj: number } },
   *   automationPotential: number,
   *   industryName: string
   * }}
   */
  const calculateWasteAndSavings = (industryId, employeeCount, annualRevenue, automationMaturity, processMaturity, overrideSelectedGeneralAgents, overrideSelectedIndustryTasks) => {
    const industry = industryData.find(i => i.id === industryId);
    if (!industry) return null;

    // Calculate industry waste percentage based on automation maturity (1-10 scale)
    // 1 = highest automation (wasteLow), 10 = lowest automation (wasteHigh)
    const industryWastePercent = industry.wasteLow + 
      ((10 - automationMaturity) / 9) * (industry.wasteHigh - industry.wasteLow);
    
    // Admin (general) waste depends on Process Orientation (1-10):
    // 1 -> 30% waste, 10 -> 20% waste, linear mapping
    const adminWaste = 0.30 - ((processMaturity - 1) / 9) * 0.10; // 0.30 down to 0.20
    const adminWasteLow = adminWaste;
    const adminWasteHigh = adminWaste;

    // Calculate total waste by multiplying industry waste by admin waste (dynamic, for diagnostics)
    const totalWaste = {
      low: annualRevenue * (industry.wasteLow * adminWasteLow),
      high: annualRevenue * (industry.wasteHigh * adminWasteHigh)
    };

    // AI Agents - Average savings panel should ALWAYS use fixed 20%/30% admin waste
    const aiAdminLow = 0.20;
    const aiAdminHigh = 0.30;
    const aiAgentsAverage = {
      percentLow: industry.wasteLow * aiAdminLow,
      percentHigh: industry.wasteHigh * aiAdminHigh,
      amountLow: annualRevenue * (industry.wasteLow * aiAdminLow),
      amountHigh: annualRevenue * (industry.wasteHigh * aiAdminHigh)
    };
    
    // Calculate waste percentages for display
    const wastePercentages = {
      industryLow: industry.wasteLow * 100,
      industryHigh: industry.wasteHigh * 100,
      adminLow: adminWasteLow * 100,
      adminHigh: adminWasteHigh * 100,
      totalLow: (industry.wasteLow * adminWasteLow) * 100,
      totalHigh: (industry.wasteHigh * adminWasteHigh) * 100
    };

    // Potential Annual Savings with Automation: when both sliders at 1,
    // should be at the HIGHEST end of AI Agents Average savings.
    // Interpolate within [AI base low, AI base high] using both sliders.
    const baseLowAmount = aiAgentsAverage.amountLow;
    const baseHighAmount = aiAgentsAverage.amountHigh;
    const autoNorm = (10 - automationMaturity) / 9;   // 1 at level 1, 0 at level 10
    const procNorm = (10 - processMaturity) / 9;      // 1 at level 1, 0 at level 10
    const sLow = Math.min(autoNorm, procNorm);
    const sHigh = Math.max(autoNorm, procNorm);
    const potentialSavings = {
      low: baseLowAmount + (baseHighAmount - baseLowAmount) * sLow,
      high: baseLowAmount + (baseHighAmount - baseLowAmount) * sHigh
    };

    // Calculate equivalent FTE savings (assuming average employee cost of $50k)
    const avgEmployeeCost = 50000;
    const fteSavings = {
      low: potentialSavings.low / avgEmployeeCost,
      high: potentialSavings.high / avgEmployeeCost
    };

    // Agent-driven impact based on selected agents (support first-run overrides)
    // Sum up selected agent indices -> objects with timeSavedFTE and costImpactRate.
    const effectiveGAIdx = Array.isArray(overrideSelectedGeneralAgents) ? overrideSelectedGeneralAgents : (selectedGeneralAgents || []);
    const effectiveIAIdx = Array.isArray(overrideSelectedIndustryTasks) ? overrideSelectedIndustryTasks : (selectedIndustryTasks || []);
    const selectedGAObjects = (effectiveGAIdx || [])
      .map(i => generalAgents[i])
      .filter(Boolean);
    const selectedIAObjects = (industry && Array.isArray(industry.agents))
      ? (effectiveIAIdx || []).map(i => industry.agents[i]).filter(Boolean)
      : [];
    const totalTimeSavedFTE = [...selectedGAObjects, ...selectedIAObjects]
      .reduce((sum, a) => sum + (a.timeSavedFTE || 0), 0);
    // Revenue potential reflects agents' ability to increase revenue (capacity or sales)
    const totalRevenuePotential = [...selectedGAObjects, ...selectedIAObjects]
      .reduce((sum, a) => sum + (a.revenuePotential || 0), 0);
    const allSelectedAgents = [...selectedGAObjects, ...selectedIAObjects];
    const employeeSatisfactionImprovement = allSelectedAgents.length
      ? (allSelectedAgents.reduce((sum, a) => sum + (typeof a.jobSatisfaction === 'number' ? a.jobSatisfaction : 0), 0) / allSelectedAgents.length)
      : 0;
    // Conservative caps
    const timeSavedCap = 0.60; // max 60% FTE time savings
    const revenueImpactCap = 0.05; // max +5% revenue impact from agents
    const timeSavedAdj = Math.min(totalTimeSavedFTE, timeSavedCap);
    const revenuePotentialAdj = Math.min(totalRevenuePotential, revenueImpactCap);
    // Apply automation factor for agent-driven impacts (kept as-is)
    const automationFactor = 0.60 - ((automationMaturity - 1) / 9) * 0.45; // 0.60 -> 0.15
    const fteSavedFromAgents = employeeCount * timeSavedAdj * automationFactor;
    // Align Labor Savings with the Estimated Annual Savings shown in the UI
    const laborSavingsFromAgents = annualRevenue * (industryWastePercent * adminWasteLow);
    const revenueImpactFromAgents = annualRevenue * revenuePotentialAdj * automationFactor;
    const agentImpact = {
      laborSavings: laborSavingsFromAgents,
      revenueImpact: revenueImpactFromAgents,
      fteSaved: fteSavedFromAgents,
      employeeSatisfactionImprovement, // 0..1 where 1 = 100% dissatisfaction relief potential
      // Expose Task Improvement as percentage-like fraction 0..1 for downstream UIs
      taskImprovement: employeeSatisfactionImprovement,
      total: laborSavingsFromAgents + revenueImpactFromAgents,
      inputs: { selectedCount: selectedGAObjects.length, timeSavedAdj, revenuePotentialAdj, automationFactor }
    };

    // Annual hours of human equivalent work (context): based on agent-driven fteSaved
    const annualHours = Math.round((fteSavedFromAgents || 0) * 2080);

    // Efficiency Score (0..100): combine inverse of automation & process maturity (higher is better efficiency)
    // This is a simple heuristic to persist into JSON for admin display
    const autoScore = ((11 - automationMaturity) / 10) * 50; // 0..50
    const procScore = ((11 - processMaturity) / 10) * 50;     // 0..50
    const efficiencyScore = Math.round(autoScore + procScore);

    // Revenue Per Employee and Industry RPE average
    const revenuePerEmployee = employeeCount > 0 ? (annualRevenue / employeeCount) : 0;
    const industryRPEAvg = getIndustryRPEAvg(industryId);

    // Headline Estimated Annual Savings: align to AI Agents average low amount
    const estimatedAnnualSavings = aiAgentsAverage.amountLow;
    // Note: formatted value uses USD/en-US as a stable reference for storage/comparison;
    // UI display should use formatCurrency() which respects user's locale and currency selection
    const estimatedAnnualSavingsFormatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(estimatedAnnualSavings);

    return {
      industryWaste: {
        percent: industryWastePercent,
        amount: annualRevenue * industryWastePercent,
        low: industry.wasteLow,
        high: industry.wasteHigh
      },
      adminWaste: {
        low: adminWasteLow,
        high: adminWasteHigh,
        amountLow: annualRevenue * adminWasteLow,
        amountHigh: annualRevenue * adminWasteHigh
      },
      totalWaste,
      aiAgentsAverage,
      wastePercentages,
      potentialSavings,
      fteSavings: { ...fteSavings, hours: annualHours },
      agentImpact,
      automationPotential: industry.automationPotential,
      industryName: industry.name,
      efficiencyScore,
      revenuePerEmployee,
      industryRPE: industryRPEAvg,
      estimatedAnnualSavings,
      estimatedAnnualSavingsFormatted
    };
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
        <h2 className="text-2xl font-bold mb-4 text-center text-green-600">{t('thankYou')}</h2>
        <p className="text-center text-black mb-6">{t('submissionSuccess')}</p>
        <ThankYouConfirmation />       
      </div>
    );
  }



  const handleRevenueChange = (e) => {
    const raw = e.target.value;
    setRevenueInput(raw);
    const parsed = parseRevenueInput(raw);
    setFormData(prev => ({ ...prev, annualRevenue: parsed }));
  };

  const handleRevenueBlur = (e) => {
    const parsed = parseRevenueInput(e.target.value);
    setRevenueInput(formatRevenueWithCommas(parsed));
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl font-bold mb-6 text-center text-blue-600">{t('roiCalculatorTitle')}</h1>
      
      {/* Detected Company Banner */}
      {detectedCompany && detectedCompany.name && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm text-blue-800">
                <span className="font-medium">We detected you're from {detectedCompany.name}</span>
                {detectedCompany.description && (
                  <span className="block text-blue-600 mt-1 text-xs">{detectedCompany.description}</span>
                )}
              </p>
              <p className="mt-2 text-xs text-blue-600">
                We've pre-filled some details based on public information. Feel free to adjust them.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setDetectedCompany(null)}
              className="ml-2 text-blue-400 hover:text-blue-600"
              aria-label="Dismiss"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Industry Selection */}
        <div>
          <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-1">
            {t('industry')}
          </label>
          <select
            id="industry"
            name="industry"
            value={formData.industry}
            onChange={handleChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            required
          >
            <option value="">{t('selectYourIndustry')}</option>
            {Object.entries(industriesByCategory).map(([category, industries]) => (
              <optgroup key={category} label={category.charAt(0).toUpperCase() + category.slice(1)}>
                {industries.map(industry => (
                  <option key={industry.id} value={industry.id}>
                    {t('industry_' + industry.id)}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        

        {/* Employee Count */}
        <div>
          <label htmlFor="employeeCount" className="block text-sm font-medium text-gray-700 mb-1">
            {t('numberOfEmployees')}
          </label>
          <div className="mt-1">
            <input
              type="range"
              id="employeeCount"
              name="employeeCount"
              min="1"
              max="1000"
              value={formData.employeeCount}
              onChange={(e) => {
                const val = Number(e.target.value);
                setFormData(prev => ({ ...prev, employeeCount: val }));
              }}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="mt-2 text-sm text-gray-600">
              {formData.employeeCount} {formData.employeeCount === 1 ? t('employeeSingular') : t('employeePlural')}
            </div>

            {/* Direct entry text box */}
            <div className="mt-3">
              <label htmlFor="employeeCountInput" className="block text-xs font-medium text-gray-600 mb-1">{t('enterHeadcountDirectly')}</label>
              <div className="relative rounded-md shadow-sm">
                <input
                  type="text"
                  id="employeeCountInput"
                  inputMode="numeric"
                  value={employeeInput}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setEmployeeInput(raw);
                    let parsed = parseIntegerInput(raw);
                    // Clamp to slider bounds
                    if (parsed < 1) parsed = 1;
                    if (parsed > 1000) parsed = 1000;
                    setFormData(prev => ({ ...prev, employeeCount: parsed }));
                  }}
                  onBlur={(e) => {
                    let parsed = parseIntegerInput(e.target.value);
                    if (parsed < 1) parsed = 1;
                    if (parsed > 1000) parsed = 1000;
                    setEmployeeInput(formatIntegerWithCommas(parsed));
                  }}
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3"
                  placeholder={t('employeePlaceholder')}
                  aria-describedby="employeeCount-help"
                  required
                />
              </div>
              <p id="employeeCount-help" className="mt-1 text-xs text-gray-500">{t('employeeInputHelp')}</p>
            </div>
          </div>
        </div>

        {/* Annual Revenue */}
        <div>
          <label htmlFor="annualRevenue" className="block text-sm font-medium text-gray-700 mb-1">
            {t('annualRevenue')}
          </label>
          <div className="mt-2">
            {/* Slider */}
            <input
              type="range"
              id="annualRevenueRange"
              min="0"
              max="100000000"
              step="100000"
              value={formData.annualRevenue}
              onChange={(e) => setFormData(prev => ({ ...prev, annualRevenue: Number(e.target.value) }))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between items-center mt-2">
              <span className="text-sm font-medium text-gray-700">{formatRevenueDisplay(formData.annualRevenue)}</span>
              <span className="text-xs text-gray-500">{t('revenueSliderDescriptor')}</span>
            </div>

            {/* Direct entry text box */}
            <div className="mb-4">
              <label htmlFor="annualRevenue" className="block text-sm font-medium text-gray-700 mb-1">
                {t('annualRevenue')}
              </label>
              <div className="flex gap-2">
                <div className="relative rounded-md shadow-sm flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">
                      {getCurrencySymbol(formData.currency)}
                    </span>
                  </div>
                  <input
                    type="text"
                    id="annualRevenue"
                    name="annualRevenue"
                    inputMode="numeric"
                    value={revenueInput}
                    onChange={handleRevenueChange}
                    onBlur={handleRevenueBlur}
                    className="pl-12 pr-3 block w-full border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="e.g. 750,000 or 1.5M"
                    aria-describedby="annualRevenue-help"
                    required
                  />
                </div>
                <div className="w-32">
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      currency: e.target.value
                    }))}
                    className="h-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    {availableCurrencies.map((curr) => (
                      <option key={curr.code} value={curr.code}>
                        {curr.code}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <p id="annualRevenue-help" className="mt-1 text-xs text-gray-500">
                {t('revenueInputHelp')}
              </p>
            </div>
          </div>
        </div>

        {/* Average FTE Hourly Cost */}
        <div>
          <label htmlFor="hourlyCost" className="block text-sm font-medium text-gray-700 mb-1">
            {t('averageFTEHourlyCost', 'Average FTE hourly cost')}
          </label>
          <div className="relative rounded-md shadow-sm w-full sm:max-w-xs">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 sm:text-sm">
                {getCurrencySymbol(formData.currency)}
              </span>
            </div>
            <input
              type="number"
              id="hourlyCost"
              name="hourlyCost"
              min="0"
              step="1"
              value={formData.hourlyCost}
              onChange={handleChange}
              className="pl-12 pr-3 block w-full border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="e.g. 50"
              aria-describedby="hourlyCost-help"
              required
            />
          </div>
          <p id="hourlyCost-help" className="mt-1 text-xs text-gray-500">
            {t('hourlyCostHelp', 'Used for contextual labor impact; headline totals remain range-based and allocated proportionally.')}
          </p>
        </div>

        {/* Process Orientation */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              {t('processOrientation')}
            </label>
            <span className="text-sm font-medium text-gray-500 flex flex-col items-end">
              <span className="text-base font-semibold text-gray-800">{formData.processMaturity}/10 – {getProcessLabel(formData.processMaturity, t).title}</span>
              <span className="text-xs text-gray-500 mt-1">{getProcessLabel(formData.processMaturity, t).description}</span>
            </span>
          </div>
          <input
            type="range"
            min="1"
            max="10"
            value={formData.processMaturity}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              setFormData(prev => ({ ...prev, processMaturity: val }));
            }}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>{t('processSliderLow')}</span>
            <span>{t('processSliderHigh')}</span>
          </div>
        </div>

        {/* Automation Maturity */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              {t('currentLevelOfAutomation')}
            </label>
            <span className="text-sm font-medium text-gray-500 flex flex-col items-end">
              <span className="text-base font-semibold text-gray-800">{formData.automationMaturity}/10 – {getMaturityLabel(formData.automationMaturity, t).title}</span>
              <span className="text-xs text-gray-500 mt-1">{getMaturityLabel(formData.automationMaturity, t).description}</span>
            </span>
          </div>
          <input
            type="range"
            min="1"
            max="10"
            value={formData.automationMaturity}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              setFormData(prev => ({ ...prev, automationMaturity: val }));
              setSliderTouched(true);
            }}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>{t('automationSliderLow')}</span>
            <span>{t('automationSliderHigh')}</span>
          </div>
        </div>
        
        {/* Agent sections hidden from public form UI. Data and auto-config remain for backend/report use. */}

        <div className="pt-2">
          <button
            type="submit"
            className="w-full btn-primary"
          >
            {t('calculateMySavings')}
          </button>
        </div>
      </form>

      {/* Results Section - extracted component */}
      {showResults && savings && (
        <ResultsDisplay
          savings={savings}
          formData={formData}
          locale={locale}
          formatCurrency={formatCurrency}
          t={t}
          onGetReport={() => setShowContactForm(true)}
        />
      )}

      {/* Contact Form - extracted component */}
      {showContactForm && !submitted && (
        <ContactForm
          contactInfo={formData.contactInfo}
          onChange={handleChange}
          onSubmit={handleContactSubmit}
          onContactInfoUpdate={(updates) => {
            setFormData(prev => ({
              ...prev,
              contactInfo: { ...prev.contactInfo, ...updates }
            }));
          }}
          t={t}
        />
      )}

      {/* Processing Modal - shown while generating report */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-sm mx-4 text-center shadow-xl">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('processingReport')}</h3>
            <p className="text-sm text-gray-600">{t('processingReportDesc')}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to get maturity label
function getMaturityLabel(level, t) {
  return {
    title: t(`automationLevelTitle_${level}`),
    description: t(`automationLevelDesc_${level}`)
  };
}

// Helper for process orientation label
function getProcessLabel(level, t) {
  return {
    title: t(`processLevelTitle_${level}`),
    description: t(`processLevelDesc_${level}`)
  };
}

export default ROICalculator;
